import React from 'react';
import { Box, Typography, IconButton, LinearProgress } from '@mui/material';
import { UploadedFile } from './uploaderTypes';

interface UploadFileItemProps {
  file: UploadedFile;
  onDelete: (id: string) => void;
}

/**
 * Renders a single uploaded file row.
 * Shows a progress bar while `isUploading` is true (upload to server in progress).
 * Once uploaded (isUploading: false), shows as idle until the user clicks Evaluate
 * — at which point EvaluatedFileItem takes over rendering.
 */
const UploadFileItem: React.FC<UploadFileItemProps> = ({ file, onDelete }) => {
  const isUploading = file.isUploading;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        py: 1.5,
        backgroundColor: '#FBFBFB',
        borderRadius: '8px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          title={file.name}
          sx={{
            fontSize: 15,
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 500,
            color: '#2C2E50',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {file.name}
        </Typography>

        <IconButton
          size="small"
          onClick={() => onDelete(file.id)}
          disabled={isUploading}
          sx={{ cursor: isUploading ? 'not-allowed' : 'pointer' }}
        >
          <img src="/images/trash-icon-blue.svg" alt="delete-file" width={14} height={14} />
        </IconButton>
      </Box>

      {/* Upload-in-progress indicator */}
      {isUploading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
          <Box
            component="span"
            sx={{ fontSize: 12, color: '#1A55F9', lineHeight: 1, flexShrink: 0 }}
          >
            ✦
          </Box>
          <LinearProgress
            variant="indeterminate"
            sx={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              backgroundColor: '#E8EEFF',
              '& .MuiLinearProgress-bar': { backgroundColor: '#1A55F9' },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default UploadFileItem;
